// Turns an axios error (or any thrown error) into a short, actionable
// message a non-technical instructor can read and act on, instead of a raw
// stack trace or a silently-swallowed failure. Used anywhere a course-
// creation/editing action can fail (saving a course, generating with AI,
// applying a template, saving a quiz, ...) so failures read consistently
// across the whole admin area.
export function getFriendlyErrorMessage(err, fallback = 'Something went wrong. Please try again.') {
  // Axios sets `request` whenever a request was actually sent, and `response`
  // only once the server answered. request-with-no-response is a genuine
  // network failure (backend not running, no connection, CORS, ...); that's
  // different from a plain `throw new Error(...)` in application code (e.g.
  // `throw new Error(res.message)` after a successful-but-declined request),
  // which has neither and should just show its own message below.
  if (err?.request && !err?.response) {
    return "Couldn't reach the server. Check your internet connection and that the backend is running, then try again.";
  }

  if (err?.response) {
    const { status, data } = err.response;
    // Most controllers respond with { success:false, message }; a few
    // lower-level failures (e.g. an upload rejected before reaching a
    // controller) fall through to a generic { error: { message } } shape --
    // check both so neither is silently dropped.
    const serverMessage = data?.message || data?.error?.message;

    if (status === 401) {
      return 'Your session has expired. Please log in again and retry.';
    }
    if (status === 403) {
      return serverMessage || "You don't have permission to do this.";
    }
    if (status === 413) {
      return 'That file is too large. Please use a smaller image (5MB or less) and try again.';
    }
    if (status === 404) {
      return serverMessage || 'That could not be found -- it may have been deleted.';
    }
    if (status >= 500) {
      return serverMessage || 'Something went wrong on the server. Please try again in a moment.';
    }

    return serverMessage || fallback;
  }

  // Not an HTTP error at all -- e.g. a plain Error thrown from application
  // code after a request that actually succeeded. Its own message is
  // usually the most useful thing to show.
  return err?.message || fallback;
}
